import { Signal } from "signals";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import settingsModel from "slotworks/model/settings/settings-model";
import { arrayOfValues } from "slotworks/utils/collection-utils";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Rectangle from "slotworks/utils/geom/rectangle";
import { RandomRangeInt } from "slotworks/utils/math/random";
import { Tween } from "slotworks/utils/tween";
import { Easing } from "slotworks/utils/tween";
import ReelComponent from "../../reel-component";

enum DropType {
    OUT, IN
}

export default class ReelDropper {

    public static DROP_DISTANCE: number = 1500;
    public static DROP_DURATION: number = 500;
    public static DROP_DURATION_QUICK_SPIN: number = 400;
    public static DROP_DURATION_TURBO_SPIN: number = 300;
    public static SYMBOL_STAGGER_MIN: number = 50;
    public static SYMBOL_STAGGER_MAX: number = 100;

    /**
     * Override the layer the anticipation animations are searched for in (SymbolAnimations by default)
     */
    public static anticipationLayer: Layers | Layers[];
    public static minRepeats = 7;

    public onDrop: Signal = new Signal();
    public onLand: Signal = new Signal();
    public onAnticipationStart: Signal = new Signal();
    public onAnticipationEnd: Signal = new Signal();
    public anticipating: boolean = false;
    public wasAnticipating: boolean = false;

    private reel: ReelComponent;
    private allSymbols: SymbolComponent[];
    private reelstrip: string[];

    // State
    private started: boolean = false;
    private stopping: boolean = false;
    private skipping: boolean = false;
    private held: boolean = false;
    private heldSymbols: boolean[];

    private cancelGroup: CancelGroup = new CancelGroup();

    constructor(reel: ReelComponent, reelstop: number, reelstrip: string[]) {
        this.reel = reel;
        this.reelstrip = [...reelstrip];
        this.allSymbols = [...this.reel.getSymbols()];
        this.unholdSymbols();
    }

    public start(startDelay: number = 0, jumpStart: boolean = false, quickSpin: boolean = false): Contract<void> {
        let dropOutContracts: Array<() => Contract<void>> = [];
        if (!this.held) {
            this.started = true;
            this.onDrop.dispatch(this.reel.index);
            const turboSpin: boolean = settingsModel.getTurboSpin();
            dropOutContracts = this.createDropContracts(startDelay, 0, quickSpin, turboSpin, DropType.OUT);
        }
        return new Parallel(dropOutContracts);
    }

    public stop(spinRecord: SpinRecord, stopDelay: number = 0, quickSpin: boolean): Contract<void> {
        let dropInContracts: Array<() => Contract<void>> = [];
        if (!this.held) {
            this.stopping = true;
            this.setResult(spinRecord.stops[this.reel.index], spinRecord.reelset[this.reel.index]);
            this.setSymbols(spinRecord.stops, spinRecord.reelset);
            const turboSpin: boolean = settingsModel.getTurboSpin();
            dropInContracts = this.createDropContracts(stopDelay, ReelDropper.DROP_DISTANCE * 2, quickSpin, turboSpin, DropType.IN);
        }
        return new Parallel(dropInContracts);
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        const cleanupContracts: Array<() => Contract<any>> = [];
        if (!this.held) {
            this.skipping = true;
            const contract: Contract<void> = new Contract<void>((resolve) => {
                this.cancelGroup.cancel();
                this.setResult(spinRecord.stops[this.reel.index], spinRecord.reelset[this.reel.index]);
                this.resetSymbols(spinRecord.stops, spinRecord.reelset);
                resolve(null);
            });
            cleanupContracts.push(() => contract);
        }
        return new Parallel(cleanupContracts);
    }

    public anticipate(index: number, reelstrip: string[]): Contract<void> {
        return new Contract<void>((resolve) => {
            resolve(null);
        });
    }

    public hold(hold: boolean) {
        this.held = hold;
    }

    public holdSymbol(index: number, hold: boolean): void {
        this.heldSymbols[index] = hold;
    }

    public isStopping() {
        return this.stopping;
    }

    public getReelStrip(): string[] {
        return this.reelstrip;
    }

    public getStarted(): boolean {
        return this.started;
    }

    public getSkipping(): boolean {
        return this.skipping;
    }

    private createDropContracts(startDelay: number, startOffsetY: number, quickSpin: boolean,
                                turboSpin: boolean, dropType: DropType): Array<() => Contract<void>> {

        startDelay = quickSpin || turboSpin ? 0 : startDelay;

        const speedMultiplier: number = turboSpin ? 0.5 : quickSpin ? 0.75 : 1;
        const dropDuration: number = ReelDropper.DROP_DURATION * speedMultiplier;
        const symbolStaggerMin: number = ReelDropper.SYMBOL_STAGGER_MIN * speedMultiplier;
        const symbolStaggerMax: number = ReelDropper.SYMBOL_STAGGER_MAX * speedMultiplier;

        const dropContracts: Array<() => Contract<void>> = [];
        let staggerDelay: number = 0;
        if (!this.held) {
            for (let i: number = this.allSymbols.length - 1; i >= 0; i--) {
                if (!this.heldSymbols[i]) {
                    const symbol: SymbolComponent = this.allSymbols[i];
                    const symbolTransform: Rectangle = symbol.getTransform().clone();
                    const transformRect: Rectangle = new Rectangle(symbolTransform.x, symbolTransform.y - startOffsetY, symbolTransform.width, symbolTransform.height);
                    staggerDelay += RandomRangeInt(symbolStaggerMin ? symbolStaggerMin : 0, symbolStaggerMax ? symbolStaggerMax : 100);
                    const dropContract = this.createDropTweenContract(symbol, transformRect, ReelDropper.DROP_DISTANCE, dropDuration, startDelay + staggerDelay, dropType, i === 0);
                    dropContracts.push(() => dropContract);
                }
            }
        }
        return dropContracts;
    }

    private createDropTweenContract(symbol: SymbolComponent, startTransform: Rectangle, distance: number,
                                    duration: number, delay: number, dropType: DropType, last: boolean): Contract<void> {

        const tween: Tween = this.cancelGroup.tween(startTransform);
        tween.to({ y: startTransform.y + distance }, duration);
        tween.delay(delay);
        tween.easing(Easing.Cubic.In);
        tween.onUpdate(() => symbol.setTransform(startTransform));
        return new Contract<void>((resolve: (value?: any) => void) => {
            tween.onComplete(() => {
                if (dropType === DropType.IN) {
                    symbol.land().execute();
                }
                if (this.stopping && last) {
                    this.onLand.dispatch(this.reel.index);
                }
                resolve(null);
            });
            tween.start();
        });
    }

    private setResult(index: number, reelstrip: string[]): void {
        this.reelstrip = reelstrip;
    }

    private resetSymbols(stops: number[], reelset: string[][]): void {
        this.setSymbols(stops, reelset);
        this.reel.resetSymbolPositions();
        this.allSymbols.forEach((symbol: SymbolComponent, i: number) => {
            symbol.updateTransform();
            symbol.setVisible(true);
        });
    }

    private setSymbols(stops: number[], reelset: string[][]): void {
        this.allSymbols.forEach((symbol: SymbolComponent, i: number) => {
            const reelIndex: number = this.reel.index - 1;
            const targetReelset: string[] = reelset[reelIndex];
            const targetStop: number = stops[reelIndex] + i;
            symbol.setSymbol(targetReelset[targetStop]);
            symbol.static();
        });
    }

    private unholdSymbols(): void {
        this.heldSymbols = arrayOfValues<boolean>(this.allSymbols.length, false);
    }
}
